import { useCallback, useContext, useMemo } from 'react'

import { BLUEPRINT_DEFAULT_SCALE, DocumentSize } from '@editor/config/constants'
import { UserContext } from '@editor/contexts/Users'
import { Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import { updateBlueprint } from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { autoCalculatePrintScale, isValidScale, scaleToString, stringToScale } from '@editor/services/PrintScale'
import { convertPrintDimension } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import * as Sentry from '@sentry/react'
import { useSelector } from 'react-redux'

// Constants for measurement label dimensions
const DASH_SIZE_POINTS = 2
const GAP_SIZE_POINTS = 1.5
const DIMENSION_SCALE_MULTIPLIER = 1000

/**
 * Custom hook for managing blueprint print scale functionality.
 */
export const usePrintScale = () => {
  const dispatch = useAppDispatch()
  const { getAccessToken } = useContext(UserContext)

  // Store selectors
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)
  const pcdBoundingBox = useSelector((state: RootState) => state.blueprint.editor.pcdBoundingBox)
  const pcd2dBoundingBox = useSelector((state: RootState) => state.blueprint.editor.pcd2dBoundingBox)
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)
  const isAllowedToModify = useSelector((state: RootState) => state.blueprint.editor.isAllowedToModify)

  // Current print scale (with fallback to calculated default)
  const currentPrintScale = useMemo(() => {
    if (selectedBlueprint?.print_scale !== undefined) {
      return selectedBlueprint.print_scale
    }

    // Calculate default if no print scale is set and we have bounding box
    // Use 2D bounding box for 2D mode, fallback to 3D bounding box
    const boundingBoxToUse = mode === Mode.Editor2D && pcd2dBoundingBox ? pcd2dBoundingBox : pcdBoundingBox
    if (boundingBoxToUse) {
      return autoCalculatePrintScale(boundingBoxToUse, 'A4')
    }

    return BLUEPRINT_DEFAULT_SCALE
  }, [selectedBlueprint?.print_scale, pcdBoundingBox, pcd2dBoundingBox, mode])

  // Current scale as display string (e.g., "100" for 1:100)
  const currentScaleString = useMemo(() => scaleToString(currentPrintScale), [currentPrintScale])

  /**
   * Calculate the default print scale for a given document size.
   */
  const calculateScaleForDocumentSize = useCallback(
    (documentSize: DocumentSize): number => {
      // Use 2D bounding box for 2D mode, fallback to 3D bounding box
      const boundingBoxToUse = mode === Mode.Editor2D && pcd2dBoundingBox ? pcd2dBoundingBox : pcdBoundingBox
      if (!boundingBoxToUse) return 0.01 // Default if no bounding box

      return autoCalculatePrintScale(boundingBoxToUse, documentSize)
    },
    [pcdBoundingBox, pcd2dBoundingBox, mode],
  )

  /**
   * Update the blueprint's print scale.
   *
   * @param newScale - The new print scale to set.
   */
  const updatePrintScale = useCallback(
    async (newScale: number): Promise<boolean> => {
      if (!project || !inspectionArea || !selectedBlueprint?.sort_key || !isAllowedToModify) {
        return false
      }

      if (!isValidScale(newScale)) {
        return false
      }

      try {
        const token = await getAccessToken()
        if (!token) return false

        const result = await dispatch(
          updateBlueprint({
            accessToken: token,
            projectId: project.project_id,
            areaId: inspectionArea.inspection_area_id,
            blueprint: {
              ...selectedBlueprint,
              print_scale: newScale,
            },
          }),
        ).unwrap()

        return !!result
      } catch (error) {
        Sentry.captureException(error)
        return false
      }
    },
    [project, inspectionArea, selectedBlueprint, isAllowedToModify, getAccessToken, dispatch],
  )

  /**
   * Update print scale from a scale string (e.g., "100" for 1:100).
   *
   * @param scaleString - The scale string to convert and update.
   */
  const updatePrintScaleFromString = useCallback(
    async (scaleString: string): Promise<boolean> => updatePrintScale(stringToScale(scaleString)),
    [updatePrintScale],
  )

  /**
   * Calculate dash and gap sizes for offset lines based on print scale
   * @returns Object containing dashSize and gapSize in Three.js world units, scaled for material usage
   */
  const calculateDashGapSizes = useCallback(() => {
    const dashSize = convertPrintDimension(DASH_SIZE_POINTS, currentPrintScale) * DIMENSION_SCALE_MULTIPLIER
    const gapSize = convertPrintDimension(GAP_SIZE_POINTS, currentPrintScale) * DIMENSION_SCALE_MULTIPLIER
    return { dashSize, gapSize }
  }, [currentPrintScale])

  return {
    // Current state
    currentPrintScale,
    currentScaleString,
    canModify: isAllowedToModify,
    hasBoundingBox: !!(mode === Mode.Editor2D && pcd2dBoundingBox ? pcd2dBoundingBox : pcdBoundingBox),

    // Actions
    updatePrintScale,
    updatePrintScaleFromString,
    calculateScaleForDocumentSize,
    calculateDashGapSizes,

    // Utilities
    scaleToString,
    stringToScale,
    isValidScale,
  }
}
